<template>
    <div>
        <QTable
            v-model:pagination="pagination"
            class="my-sticky-header-table"
            :rows="rows"
            :columns="columns"
            :filter="search"
            :rowsPerPageOptions="rowsPerPageOptions"
            :loading="tableIsLoading"
            rowKey="name"
            flat
            outlined
            separator="vertical"
            bordered
            @request="emitRequest"
        >
            <template #body-cell="props">
                <QTd :props="props">
                    <template v-if="isUrl(props.value)">
                        <a :href="props.value" target="_blank" rel="noopener noreferrer">{{ props.value }}</a>
                    </template>
                    <template v-else>
                        <span
                            class="cell-content"
                            tabindex="0"
                            @click.stop="openPopover($event, props)"
                            @keydown.enter.stop="openPopover($event, props)"
                        >
                            <QTooltip
                                v-if="String(props.value).length > 20"
                                anchor="top middle"
                            >
                                {{ props.value }}
                            </QTooltip>
                            {{ props.value }}
                        </span>
                    </template>
                </QTd>
            </template>
            <template #body-cell-start="props">
                <QTd :props="props">
                    {{ App.dateLocationFormat(props.value, 'long') }}
                </QTd>
            </template>
            <template #body-cell-end="props">
                <QTd :props="props">
                    {{ App.dateLocationFormat(props.value, 'long') }}
                </QTd>
            </template>
            <template #top-left>
                <WText
                    :size="14"
                    color="primary"
                >
                    <WText
                        :size="16"
                        tag="span"
                        weight="bold"
                        color="primary"
                    >
                        {{ $t('visualization.traces.table.total', { rowsNumber, filteredTraces }) }}
                    </WText>
                    {{ $t('visualization.traces.table.totalDesc') }}
                </WText>
            </template>
            <template #top-right />
            <template #no-data="{ message }">
                <div class="full-width row flex-center text-accent q-gutter-sm">
                    <span>
                        {{ message }}
                    </span>
                </div>
            </template>
        </QTable>
        <QMenu
            v-if="popoverAnchor"
            v-model="showPopover"
            :target="popoverAnchor"
            maxWidth="500px"
            class="q-pa-sm"
        >
            <div style="white-space: pre-wrap; word-break: break-all;">
                {{ popoverValue }}
                <QBtn
                    icon="content_copy"
                    size="sm"
                    color="primary"
                    flat
                    dense
                    class="q-ml-sm"
                    @click="copyToClipboard(popoverValue)"
                >
                    <QTooltip>{{ $t('visualization.traces.copyToClipboard') }}</QTooltip>
                </QBtn>
                <QBtn
                    v-if="showTraceKeyActions"
                    flat
                    icon="filter_alt"
                    color="primary"
                    dense
                    class="q-ml-sm"
                    :disable="checkIfExistFilter(popoverRow.traceKey)"
                    @click="handleRowClick(popoverRow.traceKey)"
                >
                    <QTooltip
                        anchor="top middle"
                        self="bottom middle"
                    >
                        {{ checkIfExistFilter(popoverRow.traceKey) ? $t('visualization.traces.filterTooltipON') : $t('visualization.traces.filterTooltipOFF') }}
                    </QTooltip>
                </QBtn>
                <QBtn
                    v-if="showTraceKeyActions"
                    flat
                    dense
                    icon="visibility"
                    color="primary"
                    class="q-ml-sm"
                    @click="handlePreviewTrace(popoverRow.traceKey)"
                >
                    <QTooltip
                        anchor="top middle"
                        self="bottom middle"
                    >
                        {{ $t('visualization.traces.previewTooltip') }}
                    </QTooltip>
                </QBtn>
            </div>
        </QMenu>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { filtersStorageMixin } from '@/mixins'
import {
    Api, apiRequest, notifyError,
} from '@/api'

const ROWS_PER_PAGE_OPTIONS = [
    30, 50, 75, 100, 250, 500,
]

export default {
    name: 'TracesTable',
    mixins: [filtersStorageMixin],
    inject: ['App'],
    props: {
        processId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        filteredTraces: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        isLoading: VueTypes.bool.def(false),
        caseId: VueTypes.string,
        search: VueTypes.string.def(''),
    },
    emits: ['preview-trace'],
    data() {
        return {
            columns: [],
            rows: [],
            rowsNumber: 0,
            page: 1,
            rowsPerPage: 30,
            rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
            tableIsLoading: this.isLoading,
            popoverValue: '',
            showPopover: false,
            popoverAnchor: null,
            popoverRow: null,
            popoverColumnName: null,
        }
    },
    computed: {
        showTraceKeyActions() {
            return !!(this.popoverRow && this.popoverRow.traceKey && this.popoverColumnName === 'traceKey')
        },
        pagination: {
            get() {
                return {
                    page: this.page,
                    rowsPerPage: this.rowsPerPage,
                    rowsNumber: this.rowsNumber,
                }
            },
            set(value) {
                this.page = value.page
                this.rowsPerPage = value.rowsPerPage
            },
        },
    },
    watch: {
        isLoading(value) {
            this.tableIsLoading = value
        },
        search(value, oldValue) {
            if (`${value || ''}` === `${oldValue || ''}`) return
            this.applySearch(value)
        },
    },
    beforeMount() {
        if (this.caseId !== undefined) {
            this.applySearch(this.caseId)
            return
        }
        this.getEvents()
    },
    methods: {
        applySearch(value) {
            this.page = 1
            this.getEvents({
                page: 1,
                rowsPerPage: this.rowsPerPage,
                search: value || '',
            })
        },
        async getEvents(params) {
            const { processId } = this
            const formattedParams = params
                ? this.formatParams(params)
                : this.formatParams({
                    page: this.page,
                    rowsPerPage: this.rowsPerPage,
                    search: this.search,
                })
            this.tableIsLoading = true
            apiRequest(Api().visualizations.traces({ processId, params: formattedParams }))
                .then(({ data, total }) => {
                    if (!data.length) {
                        this.rows = data
                        this.rowsNumber = total
                        return
                    }
                    const formattedColumns = this.formatColumns(data)
                    this.columns = formattedColumns
                    this.rows = this.formatRows(data)
                    this.rows = this.rows.map((r) => ({
                        ...r,
                        start: r.start.replace('Z', ''),
                        end: r.end.replace('Z', ''),
                        // The 'traceKey' exception should depend on the type of data that should be received from the backend in the 'configurations' call
                        ...Object.fromEntries(Object.entries(r).filter(([key]) => key !== 'start' && key !== 'end' && key !== 'traceKey')
                            .map(([key, value]) => [key, this.App.numberLocationFormat(value, true)])),
                    }))
                    this.rowsNumber = total
                })
                .catch(notifyError)
                .finally(() => { this.tableIsLoading = false })
        },
        emitRequest(props) {
            const rowsPerPage = props.pagination.rowsPerPage === 0 ? 9999 : props.pagination.rowsPerPage
            this.pagination = props.pagination
            this.getEvents({ ...props.pagination, rowsPerPage, search: props.filter })
        },
        formatParams(params) {
            const { page, rowsPerPage, search } = params
            return this.buildVisualizationParams({
                ...(rowsPerPage ? { start: page - 1 } : {}),
                ...(rowsPerPage ? { limit: rowsPerPage } : {}),
                search: search || '',
            })
        },
        formatRows(data) {
            return data.map(({ attributes, ...restData }) => ({ ...restData, ...attributes }))
        },
        formatColumns(data) {
            const [{ attributes, ...rest }] = data
            return [
                ...Object.keys(rest).map((f) => this.formatField(f, 'primary')),
                ...Object.keys(attributes).map((attrKey) => this.formatField(attrKey, 'attribute')).sort((a, b) => a.name.localeCompare(b.name)),
            ]
        },
        isUrl(value) {
            return typeof value === 'string' && /^https?:\/\/\S+$/i.test(value)
        },
        formatField(field, type) {
            return {
                name: field,
                label: field,
                field,
                align: 'center',
                format: (val) => (val ?? '-'),
                headerClasses: `${type === 'primary' ? 'text-white bg-primary' : 'bg-grey-4'}`,
                classes: type === 'primary' ? 'bg-light-blue-1 ellipsis' : 'bg-grey-1 ellipsis',
                style: 'max-width: 350px',
                headerStyle: 'max-width: 300px',
            }
        },
        handlePreviewTrace(traceKey) {
            if (!traceKey) return
            this.$emit('preview-trace', { traceKey })
        },
        handleRowClick(traceKey) {
            const filter = {
                id: "TRACE_ID_IN",
                values: [traceKey],
            }
            this.$eventBus.emit('addFilter', filter)
        },
        checkIfExistFilter(traceKey) {
            return !!this.visualizationFilters.find((fltr) => fltr.id === 'TRACE_ID_IN'
                && fltr.values.includes(traceKey))
        },
        openPopover(event, props) {
            if (!event.currentTarget) return // No abrir si no hay anchor
            this.popoverValue = props.value
            this.popoverRow = props.row
            this.popoverColumnName = props?.col?.name || null
            this.popoverAnchor = event.currentTarget
            this.showPopover = true
        },
        copyToClipboard(value) {
            navigator.clipboard.writeText(value)
            this.$q.notify({
                message: this.$t('visualization.traces.copied'),
                color: 'positive',
                icon: 'content_copy',
            })
        },
    },
}
</script>
<style lang="scss" scoped>
.my-sticky-header-table {
    height: calc(100vh - $header-height - $footer-height - 90px);
    min-height: 400px; // Just in case, to avoid collapsing when the viewport is too small.
    max-width: 100%;
    &:deep(thead) {
        position: sticky;
        top: 0;
        background: white;
        z-index: 99;
    }
    .q-table__top,
    .q-table__bottom,
    thead tr:first-child th {
        background-color: #c1f4cd;
    }

    thead tr th {
        position: sticky;
        z-index: 1;
    }
    thead tr:first-child th {
        top: 0;
    }

    &.q-table--loading thead tr:last-child th {
        top: 48px;
    }

    .cell-content {
        cursor: pointer;
        display: inline-block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: middle;
    }
}

</style>
